import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/forms/input.component';
import Button from '../../../components/forms/button.component';
import Icon from '../../../components/forms/icon.component';
import useModal from '../../../hooks/use-close-modal.hook';
import { useForm } from 'react-hook-form';
import { AlertService } from '../../../services/alert.service';
import LogData from '../../../components/logs/log-data.component';

interface AddCompanyModalProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  onAdd: (companyName: string) => void;
}

function AddCompanyModal({ show, onRequestClose, onAdd }: AddCompanyModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);

  interface FormValues {
    company: {
      selected: string;
    };
  }

  const defaultValues: FormValues = {
    company: { selected: '' }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues
  });

  const onSubmit = async (data: FormValues, buttonId: string) => {
    const confirmed = await AlertService.warning(
      `<h2>${t('account-page.modal-add-account.confirmation-title')}</h2>`,
      `<div class="text-gray-700 fs-6">
        <p class="mb-2">${t('account-page.modal-add-account.confirmation-message-company')}</p>
        <ul class="list-unstyled">
          <li class="mb-2 text-start"><strong>${t('account-page.modal-add-account.label-companies')}:</strong> ${data.company.selected}</li>
        </ul>
      </div>`,
      async () => {
        onAdd(data.company.selected);
        closeModal();
        LogData(data, buttonId, window.location.href);
      },
      t('general.btn-add-modal')
    );

    if (!confirmed) return;
  };

  const closeModal = () => {
    reset(defaultValues);
    onRequestClose(false);
  };

  return (
    <div aria-hidden="true" className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)' }}>
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => {
                closeModal();
              }}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <form onSubmit={handleSubmit((data) => onSubmit(data, 'addCompany-0237456890'))}>
            <div className="modal-body  pt-0 pb-0 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h2>{t('account-page.modal-add-account.new-company-title')}</h2>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <div className="form-group">
                <Input
                  label={t('account-page.modal-add-account.label-companies')}
                  requiredIndicator="required"
                  placeholder={t('account-page.modal-add-account.placeholder-new-company')}
                  errors={errors?.company?.selected}
                  {...register('company.selected', {
                    required: true,
                    setValueAs: (value) => value.trim()
                  })}
                />
              </div>
            </div>
            <div className="d-flex flex-center flex-row-fluid py-12 px-xl-20">
              <button
                type="button"
                className="btn btn-light hover-scale"
                onClick={() => closeModal()}
              >
                {t('general.close')}
              </button>
              <Button type="submit" id="sendData" className="btn btn-primary ms-3 hover-scale">
                {t('general.btn-add-modal')}
                <i className="ki-duotone ki-plus ms-2 fs-1">
                  <span className="path1"></span>
                  <span className="path2"></span>
                </i>
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddCompanyModal;
